// Content overlay: fetches CMS-managed blocks once and overlays them on top of
// the static defaults shipped in src/data. If the API is unreachable or a block
// is unset, the static default is used — so the design/content never breaks.
import { createContext, useContext, useEffect, useState } from "react";
import { fetchContent } from "@/lib/api";
import { SERVICE_PAGES as STATIC_SERVICE_PAGES, MENU as STATIC_MENU, SIMPLE_LINKS as STATIC_SIMPLE_LINKS } from "@/data/site";
// NB: the heavy static data (built-in articles, legal text) deliberately does
// NOT get imported here — this module sits in the critical-path main bundle.
// See lib/resourcesContent.js and lib/legalContent.js.

// Module-level cache so non-React consumers (e.g. Seo effect) can read overrides.
let _cache = {};

export function getBlock(key) {
  return _cache ? _cache[key] : undefined;
}

const ContentContext = createContext({ content: {}, loaded: false });

// True when this document is rendered inside the admin's live-preview iframe.
const IS_PREVIEW = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("cliPreview") === "1";
export const isPreviewMode = () => IS_PREVIEW;

export function ContentProvider({ children }) {
  const [content, setContent] = useState(_cache);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchContent()
      .then((data) => {
        if (!alive) return;
        _cache = data || {};
        setContent(_cache);
      })
      .catch(() => {
        /* keep static fallback */
      })
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Live-preview: accept draft content blocks from the admin editor (parent
  // window) and apply them instantly, so edits render live before saving.
  useEffect(() => {
    if (!IS_PREVIEW) return;
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data;
      if (d && d.type === "cli-preview" && d.blocks && typeof d.blocks === "object") {
        _cache = { ..._cache, ...d.blocks };
        setContent(_cache);
      }
    };
    window.addEventListener("message", onMsg);
    // Tell the parent we're ready to receive the draft.
    try { window.parent?.postMessage({ type: "cli-preview-ready" }, window.location.origin); } catch { /* ignore */ }
    return () => window.removeEventListener("message", onMsg);
  }, []);

  return <ContentContext.Provider value={{ content, loaded, preview: IS_PREVIEW }}>{children}</ContentContext.Provider>;
}

export function useContent() {
  return useContext(ContentContext);
}

// Returns the DB value for `key`, or `fallback` when unset.
export function useBlock(key, fallback) {
  const { content } = useContent();
  const v = content ? content[key] : undefined;
  return v === undefined || v === null ? fallback : v;
}

// Replace every exact-string occurrence of `oldText` with `newText` anywhere in
// a nested value. Used by the live canvas's double-click "edit on the page":
// the edited text is matched back to the field(s) holding it.
export function replaceInObject(o, oldText, newText) {
  if (typeof o === "string") return o === oldText ? newText : o;
  if (Array.isArray(o)) return o.map((x) => replaceInObject(x, oldText, newText));
  if (o && typeof o === "object") {
    const out = {};
    for (const k of Object.keys(o)) out[k] = replaceInObject(o[k], oldText, newText);
    return out;
  }
  return o;
}

// Deep-merge an admin override onto a static default so a partial edit keeps
// everything else from code (and a blank override renders the built-in exactly).
// Arrays are replaced wholesale when the override provides one (so add/remove of
// list items works), otherwise the default array is kept.
export function deepMerge(base, ov) {
  if (ov === undefined || ov === null) return base;
  if (Array.isArray(base) || Array.isArray(ov)) return ov;
  if (typeof base !== "object" || typeof ov !== "object") return ov;
  const out = { ...base };
  for (const k of Object.keys(ov)) {
    out[k] = k in base ? deepMerge(base[k], ov[k]) : ov[k];
  }
  return out;
}

// Universal page-data overlay: overlays an admin-managed `pd_<key>` block on top
// of a page's static default so every field becomes editable. Blank override →
// the built-in default (design unchanged).
export function usePageData(key, staticDefault) {
  const ov = useBlock(`pd_${key}`, null);
  return ov && typeof ov === "object" ? deepMerge(staticDefault, ov) : staticDefault;
}

// Settings are shallow-merged so a partial override (e.g. just the phone) still
// keeps the remaining static fields.
export function useSettings(staticDefault) {
  const override = useBlock("settings", null);
  if (!override || typeof override !== "object") return staticDefault;
  return { ...staticDefault, ...override };
}

// ---- Contact form configuration ----
export const DEFAULT_CONTACT_FORM = {
  fields: {
    name: { enabled: true, required: true, label: "Full name" },
    email: { enabled: true, required: true, label: "Work email" },
    phone: { enabled: true, required: false, label: "Phone" },
    company: { enabled: false, required: false, label: "Company" },
    message: { enabled: true, required: false, label: "How can we help?" },
  },
  custom: [],
  note: "No spam. We'll only use your details to respond to your enquiry.",
  successTitle: "Request received",
  successText: "A London-based engineer will reach out shortly. Talk soon.",
};

export function useContactForm() {
  const block = useBlock("forms", null);
  const f = block && block.contact ? block.contact : null;
  if (!f) return DEFAULT_CONTACT_FORM;
  return {
    fields: {
      ...DEFAULT_CONTACT_FORM.fields,
      ...(f.fields || {}),
      // Name + email are always on and required (the backend requires them).
      name: { enabled: true, required: true, label: (f.fields?.name?.label) || "Full name" },
      email: { enabled: true, required: true, label: (f.fields?.email?.label) || "Work email" },
    },
    custom: Array.isArray(f.custom) ? f.custom : [],
    note: f.note || DEFAULT_CONTACT_FORM.note,
    successTitle: f.successTitle || DEFAULT_CONTACT_FORM.successTitle,
    successText: f.successText || DEFAULT_CONTACT_FORM.successText,
  };
}

// A CMS-overridable service page (used by ServicePage), merged over static.
export function useServicePage(slug) {
  const block = useBlock("services", null);
  const base = STATIC_SERVICE_PAGES[slug];
  const override = block && block.pages && block.pages[slug];
  if (!override) return base;
  return { ...base, ...override };
}

// The editable service-page slugs (those the CMS exposes).
export const SERVICE_PAGE_SLUGS = Object.keys(STATIC_SERVICE_PAGES);
export { STATIC_SERVICE_PAGES };

// Per-path SEO override merged over the site-wide default. Safe outside React too.
// Per-page content overrides for the bespoke (non-template) service pages.
// Returns the stored override object for a slug (or {}). Pages merge these over
// their built-in defaults, so with nothing stored the page renders unchanged.
export function usePageContent(slug) {
  const block = useBlock("pages", null);
  const o = block && typeof block === "object" ? block[slug] : null;
  return o && typeof o === "object" ? o : EMPTY_OVERRIDE;
}
const EMPTY_OVERRIDE = {};

// Overlay an array of text overrides onto a base array by index, keeping each
// base item's non-text fields (icon, image, href). The override drives the
// length, so adding or removing cards in the admin is reflected on the page;
// items added beyond the built-in list simply inherit no icon/image. With no
// override the built-in array renders unchanged (design-identical).
export function mergeByIndex(base, ov, keys = ["t", "d"]) {
  const baseArr = Array.isArray(base) ? base : [];
  if (!Array.isArray(ov) || !ov.length) return base;
  // Merge across the LONGER of the two lists: a partial override (e.g. the
  // admin saved edits to only the first card) must never drop the remaining
  // base items — that truncation blanked whole sections on the live site.
  const len = Math.max(baseArr.length, ov.length);
  return Array.from({ length: len }, (_, i) => {
    const merged = { ...(baseArr[i] || {}) };
    const o = ov[i];
    if (o) keys.forEach((k) => { if (o[k] != null) merged[k] = o[k]; });
    return merged;
  });
}

// ---- Navigation menu overlay ----------------------------------------------
// The header/footer nav is driven by MENU/SIMPLE_LINKS in src/data/site. The
// admin can override the editable TEXT + LINKS (labels, descriptions, hrefs)
// while icons and layout stay in code — so an empty override renders identically.
// Link fields must never be blanked by an override — an empty/whitespace href
// stored in the CMS would turn the menu item into a dead <Link to="">, so we
// keep the built-in (static) link instead. Text fields may still be overridden.
const _LINK_KEYS = new Set(["href"]);
const _pick = (o, keys) => {
  const out = {};
  keys.forEach((k) => {
    if (!o || o[k] == null) return;
    if (_LINK_KEYS.has(k) && typeof o[k] === "string" && o[k].trim() === "") return;
    out[k] = o[k];
  });
  return out;
};
const ITEM_KEYS = ["title", "desc", "href"];

function overlayEntries(base, ov) {
  if (!ov || !ov.entries) return base;
  return base.map((entry) => {
    const o = ov.entries[entry.key];
    if (!o) return entry;
    const m = { ...entry, ..._pick(o, ["label", "navLabel", "eyebrow", "href"]) };
    if (entry.flagship && o.flagship) m.flagship = { ...entry.flagship, ..._pick(o.flagship, ["badge", "desc", "cta", "href"]) };
    if (Array.isArray(entry.items) && Array.isArray(o.items)) {
      m.items = entry.items.map((it, i) => (o.items[i] ? { ...it, ..._pick(o.items[i], ITEM_KEYS) } : it));
    }
    if (Array.isArray(entry.columns) && Array.isArray(o.columns)) {
      m.columns = entry.columns.map((col, ci) => {
        const oc = o.columns[ci];
        if (!oc) return col;
        const mc = { ...col, ..._pick(oc, ["head", "title", "blurb"]) };
        if (Array.isArray(col.items) && Array.isArray(oc.items)) {
          mc.items = col.items.map((it, i) => (oc.items[i] ? { ...it, ..._pick(oc.items[i], ITEM_KEYS) } : it));
        }
        return mc;
      });
    }
    return m;
  });
}

function overlaySimple(base, ov) {
  if (!ov || !Array.isArray(ov.simple)) return base;
  // The stored list fully replaces simple links (they carry no icons), so add
  // /remove works; fall back to the static list when nothing is stored.
  return ov.simple
    .map((l) => ({ label: l.label || "", href: l.href || "#" }))
    .filter((l) => l.label);
}

// Returns { menu, simpleLinks } — the overlaid navigation for header/footer.
export function useMenu() {
  const block = useBlock("menu", null);
  return {
    menu: overlayEntries(STATIC_MENU, block),
    simpleLinks: overlaySimple(STATIC_SIMPLE_LINKS, block),
  };
}

// ---- Custom (admin-built) pages --------------------------------------------
// Fully data-driven pages assembled from blocks in the admin. Drafts are hidden
// from the public site. Shape: { order:[slug], pages:{ [slug]: {title, seo, status, blocks:[]} } }.
export function useCustomPages() {
  const block = useBlock("customPages", null);
  if (!block || typeof block !== "object") return { order: [], pages: {} };
  const pages = block.pages && typeof block.pages === "object" ? block.pages : {};
  const order = Array.isArray(block.order) ? block.order.filter((s) => pages[s]) : Object.keys(pages);
  return { order, pages };
}

// A single published custom page by slug (null if missing or draft).
export function useCustomPage(slug) {
  const { pages } = useCustomPages();
  const p = pages[slug];
  if (!p || typeof p !== "object") return null;
  if (p.status === "draft") return null;
  return p;
}

// Pure overlay (for the admin editor, which has the block in local state).
export function applyMenuOverlay(block) {
  return {
    menu: overlayEntries(STATIC_MENU, block),
    simpleLinks: overlaySimple(STATIC_SIMPLE_LINKS, block),
  };
}

export { STATIC_MENU, STATIC_SIMPLE_LINKS };

// ---- Redirects (old indexed URLs → new URLs) -------------------------------
const _normPath = (p) => {
  if (!p) return "/";
  let s = String(p).trim();
  if (/^https?:\/\//i.test(s)) return s; // external target, leave as-is
  if (!s.startsWith("/")) s = `/${s}`;
  s = s.split(/[?#]/)[0]; // ignore query/hash when matching
  if (s.length > 1) s = s.replace(/\/+$/, ""); // drop trailing slash
  return s.toLowerCase();
};

export function useRedirects() {
  const block = useBlock("redirects", null);
  const rules = block && Array.isArray(block.rules) ? block.rules : [];
  return rules.filter((r) => r && r.from && r.to);
}

// Returns the destination for a path, or null if no redirect applies.
export function matchRedirect(pathname, rules) {
  if (!Array.isArray(rules)) return null;
  const from = _normPath(pathname);
  const hit = rules.find((r) => _normPath(r.from) === from);
  return hit ? hit.to : null;
}

export function getSeoOverride(path) {
  const seo = getBlock("seo");
  if (!seo || typeof seo !== "object") return {};
  const base = seo.default && typeof seo.default === "object" ? seo.default : {};
  const page = seo.pages && seo.pages[path] ? seo.pages[path] : {};
  return { ...base, ...page };
}

// ---- A/B testing -----------------------------------------------------------
// Experiments block shape: { tests: { [id]: { enabled, variants: [{ key, weight,
// ...fields }] } } }. When a test is disabled or missing, useExperiment returns
// null so callers fall back to their built-in content (design unchanged).
const AB_KEY = (id) => `cli_ab_${id}`;

function pickWeighted(variants) {
  const weights = variants.map((v) => (Number(v.weight) > 0 ? Number(v.weight) : 1));
  const total = weights.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < variants.length; i++) {
    r -= weights[i];
    if (r < 0) return variants[i];
  }
  return variants[variants.length - 1];
}

// Assign (and remember) a variant for experiment `id`. Fires a one-per-session
// "view" exposure beacon. Returns the chosen variant object, or null when the
// test isn't active. Import recordAb lazily to avoid a cycle with api.js.
export function useExperiment(id) {
  const block = useBlock("experiments", null);
  const test = block && block.tests ? block.tests[id] : null;
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    if (!test || !test.enabled || !Array.isArray(test.variants) || test.variants.length < 2) {
      setVariant(null);
      return;
    }
    const valid = test.variants.filter((v) => v && v.key);
    if (valid.length < 2) { setVariant(null); return; }
    let key = null;
    try { key = localStorage.getItem(AB_KEY(id)); } catch { /* ignore */ }
    let chosen = key ? valid.find((v) => v.key === key) : null;
    if (!chosen) {
      chosen = pickWeighted(valid);
      try { localStorage.setItem(AB_KEY(id), chosen.key); } catch { /* ignore */ }
    }
    setVariant(chosen);
    // One exposure per browser-session per experiment.
    let seen = false;
    try { seen = sessionStorage.getItem(`${AB_KEY(id)}_seen`) === "1"; } catch { /* ignore */ }
    if (!seen) {
      try { sessionStorage.setItem(`${AB_KEY(id)}_seen`, "1"); } catch { /* ignore */ }
      import("@/lib/api").then((m) => m.recordAb(id, chosen.key, "view")).catch(() => {});
    }
  }, [id, test]);

  return variant;
}
